'use client';

import { CheckCircle2, XCircle, Loader2, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export type EmailValidationStatusValue = 'pending' | 'valid' | 'invalid' | 'unknown';

/**
 * The email-deliverability status chip — an icon + short word, colored by state.
 * This is the treatment used in the outreach composer's "To:" row; sharing it
 * here keeps the contact-header email list consistent with it (instead of a flat
 * gray pill).
 *
 * `showUnresolved` controls whether the ambiguous states render at all: the
 * header only wants to surface a REAL result, so it leaves this false and shows
 * nothing while pending/unknown (avoids a stuck "Checking" that never resolves).
 */
export function EmailValidationStatus({
  status,
  showUnresolved = false,
  className,
}: {
  status?: EmailValidationStatusValue | null;
  /** When false (default), pending/unknown render nothing. */
  showUnresolved?: boolean;
  className?: string;
}) {
  if (!status) return null;

  if (status === 'valid') {
    return (
      <span className={cn('inline-flex items-center gap-1 text-2xs font-medium text-green-700', className)}>
        <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
        Deliverable
      </span>
    );
  }
  if (status === 'invalid') {
    return (
      <span className={cn('inline-flex items-center gap-1 text-2xs font-medium text-red-700', className)}>
        <XCircle className="h-3.5 w-3.5 text-red-600" />
        Possible issue
      </span>
    );
  }

  if (!showUnresolved) return null;

  if (status === 'pending') {
    return (
      <span className={cn('inline-flex items-center gap-1 text-2xs font-medium text-blue-700', className)}>
        <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
        Checking
      </span>
    );
  }
  return (
    <span className={cn('inline-flex items-center gap-1 text-2xs font-medium text-muted-foreground', className)}>
      <Info className="h-3.5 w-3.5" />
      Unknown
    </span>
  );
}
